import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {signInWithEmailAndPassword, getMultiFactorResolver, MultiFactorError} from 'firebase/auth'
import {auth, db} from '../../../../firebase'
import {collection, getDocs, query, where, getFirestore, doc, setDoc} from 'firebase/firestore'
import {useAuth} from '../core/Auth'
import {logger} from '../../../utils/logger'
import {signInWithPasskey, passkeysSupported, isPasskeyCancel} from '../passkeys'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [pkLoading, setPkLoading] = useState(false)
  const [pkError, setPkError] = useState('')
  const {saveAuth, setMfaResolver} = useAuth()
  const navigate = useNavigate()

  // Passkey (Face ID / Touch ID / Windows Hello) sign-in — additive fast path.
  // The password form below stays as the recovery path. On success the helper
  // redirects; a user-cancelled biometric is silently ignored.
  const handlePasskey = async () => {
    setPkError('')
    setPkLoading(true)
    try {
      await signInWithPasskey()
    } catch (err: any) {
      if (!isPasskeyCancel(err)) {
        setPkError(err?.message || 'Passkey sign-in failed — use your password below.')
      }
    } finally {
      setPkLoading(false)
    }
  }

  // ── Auth flow preserved exactly (Firebase sign-in + admin claim + Firestore
  //    user doc + MFA resolver + preferred-homepage redirect). Presentation only
  //    was redesigned. ─────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      logger.info('Login attempt initiated.', {
        source: 'Login.onSubmit',
        module: 'Authentication',
        action: 'LOGIN_ATTEMPT',
        userEmail: values.email,
      })
      try {
        const res: any = await signInWithEmailAndPassword(auth, values.email, values.password)
        const user = await res.user
        const useruid = user.uid

        logger.info('Firebase authentication successful.', {
          source: 'Login.onSubmit',
          module: 'Authentication',
          action: 'LOGIN_AUTH_SUCCESS',
          userId: useruid,
          userEmail: values.email,
        })

        localStorage.setItem('user_uid', useruid)

        const idTokenResult = await user.getIdTokenResult()
        if (!!idTokenResult.claims.isAdmin) {
          logger.info('User identified as admin from token claims.', {
            source: 'Login.onSubmit',
            module: 'Authentication',
            action: 'ADMIN_CHECK_SUCCESS',
            userId: useruid,
          })
          localStorage.setItem('role', 'admin')
        } else {
          logger.info('User identified as non-admin from token claims.', {
            source: 'Login.onSubmit',
            module: 'Authentication',
            action: 'ADMIN_CHECK_NON_ADMIN',
            userId: useruid,
          })
          localStorage.setItem('role', 'user')
        }

        const expiresIn = 1000 * 60 * 2 // 30 minutes in milliseconds
        const expirationTime = new Date().getTime() + expiresIn

        const queryUser = await query(collection(db, 'users'), where('uid', '==', user?.uid))
        const docs = await getDocs(queryUser)

        if (docs.docs && docs.docs.length > 0) {
          docs.forEach((querysnapshot) => {
            const userDataFromDb = querysnapshot.data()
            localStorage.setItem('user', JSON.stringify(userDataFromDb))

            // Reflect the doc's role into localStorage (inert UI hint only — the
            // real admin gate is the isAdmin custom claim + firestore.rules reading
            // the singular `role`). Read the SAME singular field the rules use, not
            // a plural `roles` array, so the client can't be fooled by a stale field.
            if (userDataFromDb.role === 'admin') {
              localStorage.setItem('role', 'admin')
            }

            let localData: any = localStorage.getItem('user')
            const local: any = JSON.parse(localData)
            local.expiresIn = expirationTime
            local.loginTime = new Date().getTime()
            localStorage.setItem('user', JSON.stringify(local))

            // Redirect to the user's preferred homepage (default: dashboard)
            const homepage = userDataFromDb.defaultHomepage || '/dashboard'
            window.location.href = homepage
          })
        } else {
          // User exists in Firebase Auth but has no Firestore document — auto-create one.
          // This handles users created directly in the Firebase console.
          logger.warn('User authenticated but no Firestore document found — auto-creating.', {
            source: 'Login.onSubmit',
            module: 'Authentication',
            action: 'LOGIN_FIRESTORE_DOC_MISSING',
            userId: useruid,
            userEmail: values.email,
          })

          // Auto-provision a NON-admin doc. Admin is granted only server-side
          // (console / Admin SDK sets the singular `role: 'admin'` field + the
          // isAdmin custom claim); a client-written doc must never self-grant it.
          // (Previously this wrote roles:['admin'] — a latent privilege-escalation
          // landmine; firestore.rules only locks the singular `role`.)
          const newUserData = {
            uid: useruid,
            email: values.email,
            firstName: user.displayName?.split(' ')[0] || '',
            lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
            authProvider: 'local',
            role: 'user',
            createdAt: new Date().toISOString(),
          }
          const newUserDocRef = doc(getFirestore(), 'users', useruid)
          await setDoc(newUserDocRef, newUserData)

          localStorage.setItem(
            'user',
            JSON.stringify({...newUserData, expiresIn: expirationTime, loginTime: new Date().getTime()})
          )
          localStorage.setItem('role', 'user')

          window.location.href = '/dashboard'
        }
      } catch (error: any) {
        // Handle MFA required error
        if (error.code === 'auth/multi-factor-auth-required') {
          logger.info('MFA required for login.', {
            source: 'Login.onSubmit',
            module: 'Authentication',
            action: 'LOGIN_MFA_REQUIRED',
            userEmail: values.email,
          })

          const resolver = getMultiFactorResolver(auth, error as MultiFactorError)
          setMfaResolver(resolver)
          navigate('/auth/two-factor')
          return
        }

        logger.error('Login authentication failed.', error, {
          source: 'Login.onSubmit',
          module: 'Authentication',
          action: 'LOGIN_AUTH_FAILURE',
          userEmail: values.email,
          errorCode: error.code,
        })
        saveAuth(undefined)
        setStatus('Login credentials are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form className='w-100' onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
      <h1 className='wc-auth__heading'>Sign in to Web Construct</h1>

      {formik.status ? (
        <div className='wc-auth__alert wc-auth__alert--danger' role='alert'>
          {formik.status}
        </div>
      ) : (
        <div className='wc-auth__alert wc-auth__alert--info'>
          Sign in with your <strong>Web Construct</strong> account to continue.
        </div>
      )}

      {/* Email */}
      <div className='wc-auth__group'>
        <label className='wc-auth__label' htmlFor='wc-auth-email'>
          Email
        </label>
        <input
          id='wc-auth-email'
          type='email'
          autoComplete='username'
          placeholder='you@webconstruct.com.au'
          {...formik.getFieldProps('email')}
          className={clsx('wc-auth__input', {
            'is-invalid': formik.touched.email && formik.errors.email,
          })}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='wc-auth__field-error' role='alert'>
            {formik.errors.email}
          </div>
        )}
      </div>

      {/* Password */}
      <div className='wc-auth__group'>
        <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
          <label className='wc-auth__label' htmlFor='wc-auth-password'>
            Password
          </label>
          <Link to='/auth/forgot-password' className='wc-auth__forgot'>
            Forgot password?
          </Link>
        </div>
        <input
          id='wc-auth-password'
          type='password'
          autoComplete='current-password'
          placeholder='••••••••'
          {...formik.getFieldProps('password')}
          className={clsx('wc-auth__input', {
            'is-invalid': formik.touched.password && formik.errors.password,
          })}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='wc-auth__field-error' role='alert'>
            {formik.errors.password}
          </div>
        )}
      </div>

      {/* Submit */}
      <button
        type='submit'
        id='kt_sign_in_submit'
        className='wc-auth__submit'
        disabled={formik.isSubmitting || !formik.isValid}
      >
        {!loading && <span>Sign in</span>}
        {loading && (
          <span style={{display: 'inline-flex', alignItems: 'center'}}>
            Signing in…
            <span className='spinner-border spinner-border-sm align-middle ms-2' />
          </span>
        )}
      </button>

      {/* Passkey — Face ID / Touch ID / Windows Hello (the fast path). */}
      {passkeysSupported() && (
        <>
          <div className='wc-auth__or'>
            <span>or</span>
          </div>
          <button type='button' className='wc-auth__passkey' onClick={handlePasskey} disabled={pkLoading || loading}>
            <svg width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4' />
              <path d='M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2' />
              <path d='M17.29 21.02c.12-.6.43-2.3.5-3.02' />
              <path d='M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4' />
              <path d='M8.65 22c.21-.66.45-1.32.57-2' />
              <path d='M14 13.12c0 2.38 0 6.38-1 8.88' />
              <path d='M2 16h.01' />
              <path d='M21.8 16c.2-2 .13-5.35 0-6' />
              <path d='M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2' />
            </svg>
            {pkLoading ? 'Waiting for your device…' : 'Sign in with a passkey'}
          </button>
          {pkError && (
            <div className='wc-auth__field-error' style={{textAlign: 'center', marginTop: 12}} role='alert'>
              {pkError}
            </div>
          )}
        </>
      )}
    </form>
  )
}
